import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { squareCanvas, toJpeg } from "../photoIcons";
import { Background, personMask, withBackground } from "../segment";
import "./PhotoCapture.css";

type PhotoCaptureProps = {
  /** Called with the Base64 JPEG when the player accepts the picture. */
  onUse: (base64Jpeg: string) => void;
  onClose: () => void;
};

/** Colours for a replaced background: muted, like the looks in the picker. */
const BACKGROUND_COLOURS = ["#4fa6a6", "#e8b04a", "#e58a73", "#9a86c9", "#7fb88c", "#d07a98", "#5d7388"];

/** Why the camera could not be opened, in words for the player. */
function describeError(e: unknown): string {
  const name = e && typeof e === "object" && "name" in e ? String((e as { name: unknown }).name) : "";
  if (name === "NotAllowedError" || name === "SecurityError") {
    return "The camera is blocked. Allow camera access for this site in your browser, then try again.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError") {
    return "No camera was found on this device.";
  }
  if (name === "NotReadableError") {
    return "The camera is being used by another app.";
  }
  return "The camera could not be opened.";
}

/**
 * Take a picture with the webcam and use it as your look. The picture is cut to a square and made small;
 * it is only shown to the players of this lobby and is forgotten when you leave it.
 */
export default function PhotoCapture(props: PhotoCaptureProps): ReactElement {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | undefined>(undefined);
  const [error, setError] = useState<string>("");
  const [ready, setReady] = useState(false);
  // The picture that was taken (square), the person's outline once it has been worked out, and the choice
  // of background. "result" is what would be sent.
  const [source, setSource] = useState<HTMLCanvasElement | undefined>(undefined);
  const mask = useRef<HTMLCanvasElement | undefined>(undefined);
  const [background, setBackground] = useState<"original" | "blur" | "colour">("original");
  const [colour, setColour] = useState<string>(BACKGROUND_COLOURS[0]);
  const [working, setWorking] = useState(false);
  const [backgroundError, setBackgroundError] = useState("");
  const [result, setResult] = useState<HTMLCanvasElement | undefined>(undefined);
  const shot = useMemo(() => (result ? result.toDataURL("image/jpeg", 0.9) : ""), [result]);

  const stop = useCallback(() => {
    stream.current?.getTracks().forEach((t) => t.stop());
    stream.current = undefined;
  }, []);

  const start = useCallback(async () => {
    setError("");
    setReady(false);
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setError("This browser cannot use the camera here (it needs a secure https page).");
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      stream.current = s;
      if (video.current) {
        video.current.srcObject = s;
        await video.current.play();
      }
      setReady(true);
    } catch (e) {
      setError(describeError(e));
    }
  }, []);

  useEffect(() => {
    start();
    return stop; // always switch the camera off again (its light must go out)
  }, [start, stop]);

  const take = () => {
    const v = video.current;
    if (!v || !v.videoWidth) return;
    const picture = squareCanvas(v, v.videoWidth, v.videoHeight, true);
    mask.current = undefined;
    setBackground("original");
    setBackgroundError("");
    setSource(picture);
    setResult(picture);
    stop();
  };

  const retake = () => {
    setSource(undefined);
    setResult(undefined);
    mask.current = undefined;
    setBackground("original");
    setBackgroundError("");
    start();
  };

  // Picking Blur or Colour: the person is found on the first use (this loads the model), then the background
  // is drawn again whenever the choice changes.
  const choose = async (kind: "original" | "blur" | "colour", colourChoice: string = colour) => {
    if (!source) return;
    setBackground(kind);
    setColour(colourChoice);
    setBackgroundError("");
    if (kind === "original") {
      setResult(source);
      return;
    }
    try {
      if (!mask.current) {
        setWorking(true);
        mask.current = await personMask(source);
      }
      const choice: Background = kind === "blur" ? { kind: "blur" } : { kind: "colour", colour: colourChoice };
      setResult(withBackground(source, mask.current, choice));
    } catch (e) {
      setBackground("original");
      setResult(source);
      setBackgroundError("The background could not be changed here. You can still use the photo as it is.");
    } finally {
      setWorking(false);
    }
  };

  const dialog = (
    <div className="pcap-layer" role="dialog" aria-modal="true" aria-label="Take a photo">
      <div className="pcap-card">
        <h2 className="pcap-title">Take a photo</h2>

        <div className="pcap-frame">
          {source ? (
            <img className="pcap-shot" src={shot} alt="You, as the camera saw you" />
          ) : (
            <video ref={video} className="pcap-video" playsInline muted aria-label="Camera preview" />
          )}
          {!source && !ready && !error && <div className="pcap-wait">Starting the camera...</div>}
        </div>

        {source && (
          <div className="pcap-background">
            <div className="pcap-pills" role="group" aria-label="Background">
              {(["original", "blur", "colour"] as const).map((kind) => (
                <button
                  key={kind}
                  className={"pcap-pill" + (background === kind ? " on" : "")}
                  aria-pressed={background === kind}
                  disabled={working}
                  onClick={() => choose(kind)}
                >
                  {kind === "original" ? "Original" : kind === "blur" ? "Blur" : "Colour"}
                </button>
              ))}
            </div>
            {background === "colour" && (
              <div className="pcap-swatches" role="group" aria-label="Background colour">
                {BACKGROUND_COLOURS.map((c) => (
                  <button
                    key={c}
                    className={"pcap-swatch" + (colour === c ? " on" : "")}
                    style={{ background: c }}
                    aria-label={"Background colour " + c}
                    aria-pressed={colour === c}
                    disabled={working}
                    onClick={() => choose("colour", c)}
                  />
                ))}
              </div>
            )}
            {working && <div className="pcap-working">Finding you in the picture...</div>}
            {backgroundError && (
              <p className="pcap-error" role="alert">
                {backgroundError}
              </p>
            )}
          </div>
        )}

        {error && (
          <p className="pcap-error" role="alert">
            {error}
          </p>
        )}
        <p className="pcap-note">Only the players in this lobby will see it. It is forgotten when you leave.</p>

        <div className="pcap-actions">
          {result ? (
            <>
              <button className="gold-button pcap-btn" disabled={working} onClick={() => props.onUse(toJpeg(result))}>
                <span className="gold-label">USE THIS PHOTO</span>
              </button>
              <button className="pcap-secondary" onClick={retake}>
                Retake
              </button>
            </>
          ) : error ? (
            <button className="pcap-secondary" onClick={start}>
              Try again
            </button>
          ) : (
            <button className="gold-button pcap-btn" onClick={take} disabled={!ready}>
              <span className="gold-label">TAKE PHOTO</span>
            </button>
          )}
          <button className="pcap-secondary" onClick={props.onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  // In a portal, so that the drawer's own positioning cannot affect the dialog.
  return ReactDOM.createPortal(dialog, document.body) as unknown as ReactElement;
}
